import axios from 'axios';
import { clearStoredSession, readStoredSession } from './session';
import { addSystemLog } from './systemLog';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://api-task.fly.dev',
});

const READ_TIMEOUT_MS = 15000;
const WRITE_TIMEOUT_MS = 60000;

function getRequestLabel(config = {}) {
  const method = String(config.method || 'GET').toUpperCase();
  const url = config.url || '';
  return `${method} ${url}`;
}

function getRequestUrl(config = {}) {
  const baseURL = config.baseURL || process.env.REACT_APP_API_URL || 'https://api-task.fly.dev';
  try {
    const url = new URL(config.url || '', baseURL);
    Object.entries(config.params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    });
    return url.toString();
  } catch (_error) {
    return config.url || '';
  }
}

function getRequestDuration(config = {}) {
  const startedAt = config.metadata?.startedAt;
  return startedAt ? `${Date.now() - startedAt}ms` : '';
}

function isAuthenticationRequest(config = {}) {
  const url = String(config.url || '');
  return url === '/auth/login' || url === '/auth/register';
}

function notifyExpiredSession(error) {
  if (error?.response?.status !== 401 || isAuthenticationRequest(error.config)) return;
  if (!readStoredSession()?.token) return;

  clearStoredSession();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('task-hub:session-expired'));
  }
}

api.interceptors.request.use((config) => {
  const session = readStoredSession();
  const method = String(config.method || 'get').toLowerCase();
  if (session?.token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${session.token}`
    };
  }

  config.timeout = config.timeout || (method === 'get' ? READ_TIMEOUT_MS : WRITE_TIMEOUT_MS);
  config.metadata = {
    ...(config.metadata || {}),
    startedAt: Date.now()
  };

  addSystemLog({
    type: 'request',
    message: `${getRequestLabel(config)} iniciado`,
    details: {
      url: getRequestUrl(config)
    }
  });

  return config;
});

api.interceptors.response.use(
  (response) => {
    addSystemLog({
      type: 'success',
      message: `${getRequestLabel(response.config)} concluido`,
      details: {
        status: response.status,
        tempo: getRequestDuration(response.config),
        url: getRequestUrl(response.config)
      }
    });

    return response;
  },
  (error) => {
    const fallback = 'Erro na comunicacao com a API';
    notifyExpiredSession(error);

    addSystemLog({
      type: 'error',
      message: `${getRequestLabel(error.config)} falhou`,
      details: {
        status: error?.response?.status,
        tempo: getRequestDuration(error.config),
        mensagem: getErrorMessage(error, fallback),
        url: getRequestUrl(error.config),
        resposta: error?.response?.data
      }
    });

    return Promise.reject(error);
  }
);

export function getErrorMessage(error, fallbackMessage) {
  if (error?.code === 'ECONNABORTED') {
    return 'A API demorou para responder. Tente novamente quando precisar atualizar estes dados.';
  }

  const data = error?.response?.data;
  const integrationResponse = data?.payload?.response;
  return (
    data?.detalhes ||
    data?.payload?.detalhes ||
    data?.payload?.erro ||
    data?.payload?.message ||
    integrationResponse?.detalhes ||
    integrationResponse?.error ||
    integrationResponse?.message ||
    data?.payload?.error ||
    data?.error ||
    fallbackMessage
  );
}

export function isUnauthorizedError(error) {
  return error?.response?.status === 401;
}

export default api;
